import { Amplify } from "aws-amplify";
import { signIn, confirmSignIn, resetPassword, confirmResetPassword, fetchAuthSession, signOut, type SignInOutput } from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage as amplifySessionStorage } from "aws-amplify/utils";
import type { CognitoConfig } from "../config/config";
import type { AuthDriver, AuthStep } from "./authController";

function clearChallengeStorage() {
  try {
    for (const key of ["username", "challengeName", "signInSession", "expiry"]) window.sessionStorage.removeItem(`CognitoSignInState.${key}`);
  } catch { /* Amplify can use its in-memory fallback when browser storage is unavailable. */ }
}

export function configureCognito(config: CognitoConfig): AuthDriver {
  const region = config.userPoolId.split("_")[0];
  const legacyKey = `oidc.user:https://cognito-idp.${region}.amazonaws.com/${config.userPoolId}:${config.clientId}`;
  try { window.sessionStorage.removeItem(legacyKey); window.localStorage.removeItem(legacyKey); } catch { /* Storage may be unavailable in private browsing. */ }
  clearChallengeStorage();
  Amplify.configure({ Auth: { Cognito: { userPoolId: config.userPoolId, userPoolClientId: config.clientId, loginWith: { email: true } } } });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);

  async function next(result: SignInOutput, email: string): Promise<AuthStep> {
    const step = result.nextStep;
    switch (step.signInStep) {
      case "DONE": clearChallengeStorage(); return { step: "done" };
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE": return { step: "totp" };
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": return {
        step: "setup", setupUri: step.totpSetupDetails.getSetupUri("Ever & Up Books CMS", email).toString(),
        setupKey: step.totpSetupDetails.sharedSecret,
      };
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED": return { step: "newPassword", missingAttributes: step.missingAttributes ?? [] };
      case "RESET_PASSWORD": return { step: "resetRequired" };
      case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
        if (step.allowedMFATypes?.includes("TOTP")) return next(await confirmSignIn({ challengeResponse: "TOTP" }), email);
        break;
      case "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION":
        if (step.allowedMFATypes?.includes("TOTP")) return next(await confirmSignIn({ challengeResponse: "TOTP" }), email);
        break;
    }
    throw Object.assign(new Error(), { name: "UnsupportedAuthStep" });
  }

  let activeEmail = "";
  // Queue session reads and sign-out so an in-flight refresh cannot restore tokens after logout.
  let pending: Promise<unknown> = Promise.resolve();
  const serial = <T,>(operation: () => Promise<T>): Promise<T> => {
    const result = pending.then(operation);
    pending = result.catch(() => undefined);
    return result;
  };
  return {
    getSession: () => serial(async () => {
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) return null;
      const email = session.tokens.idToken?.payload.email;
      return { accessToken: session.tokens.accessToken.toString(), email: typeof email === "string" ? email : "Signed in" };
    }),
    signIn: (email, password) => serial(async () => {
      activeEmail = email;
      return next(await signIn({ username: email, password, options: { authFlowType: "USER_SRP_AUTH" } }), email);
    }),
    confirm: (response, attributes) => serial(async () => {
      return next(await confirmSignIn({ challengeResponse: response, options: { userAttributes: attributes } }), activeEmail);
    }),
    resetPassword: (email) => serial(async () => { await resetPassword({ username: email }); }),
    confirmReset: (email, code, password) => serial(async () => { await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password }); }),
    signOut: () => serial(async () => {
      try { await signOut(); }
      finally {
        activeEmail = "";
        clearChallengeStorage();
        // Clear only this client's tokens, including when revocation fails offline.
        try {
          for (const key of Object.keys(window.sessionStorage)) {
            if (key.startsWith(`CognitoIdentityServiceProvider.${config.clientId}.`)) window.sessionStorage.removeItem(key);
          }
        } catch { /* The SDK still clears its own in-memory tokens. */ }
      }
    }),
  };
}
